import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BootcampLearning } from './BootcampLearning';
import { AuthPrompt } from './AuthPrompt';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: number;
  name: string;
  email: string;
  bootcampCompleted: boolean;
  trustScore: number;
  currentPhase: 'bootcamp' | 'verification' | 'social' | 'financial' | 'governance';
}

const API_BASE = 'https://trustpoints-api.b-9f2.workers.dev/api';

export const TrustPointsApp: React.FC = () => {
  const { isAuthenticated, user: authUser, token } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [completedConcepts, setCompletedConcepts] = useState<string[]>([]);

  // Load completed concepts from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('bootcampProgress');
    if (saved) {
      try {
        const progress = JSON.parse(saved);
        setCompletedConcepts(progress.completedConcepts || []);
      } catch (error) {
        console.error('Error loading progress:', error);
      }
    }
  }, []);

  // Load or create user
  useEffect(() => {
    const initializeUser = async () => {
      try {
        if (isAuthenticated && authUser && token) {
          // User is authenticated, get their profile
          const response = await fetch(`${API_BASE}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const userData = await response.json();
            setUser({
              id: userData.user.id,
              name: userData.user.name,
              email: userData.user.email,
              bootcampCompleted: userData.user.bootcampCompleted,
              trustScore: userData.user.trustScore,
              currentPhase: userData.user.bootcampCompleted ? 'verification' : 'bootcamp'
            });
          }
        } else {
          // Anonymous user for bootcamp
          setUser({
            id: 0, // Anonymous user
            name: 'TrustPoints Learner',
            email: 'anonymous@trustpoints.com',
            bootcampCompleted: false,
            trustScore: 0,
            currentPhase: 'bootcamp'
          });
        }
      } catch (error) {
        console.error('User initialization error:', error);
        // Fallback to anonymous user
        setUser({
          id: 0,
          name: 'Demo User',
          email: 'demo@trustpoints.com',
          bootcampCompleted: false,
          trustScore: 0,
          currentPhase: 'bootcamp'
        });
      } finally {
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [isAuthenticated, authUser, token]);

  const handleBootcampProgress = async (conceptId: string, progress: number) => {
    console.log(`Progress on ${conceptId}: ${progress}%`);
    // Could save individual progress steps here if needed
  };

  const handleBootcampComplete = async (conceptId: string) => {
    console.log(`Completed concept: ${conceptId}`);

    // Save progress locally
    const newCompletedConcepts = [...completedConcepts, conceptId];
    setCompletedConcepts(newCompletedConcepts);

    // Save to localStorage
    const progressData = {
      completedConcepts: newCompletedConcepts,
      timestamp: Date.now()
    };
    localStorage.setItem('bootcampProgress', JSON.stringify(progressData));

    // Show auth prompt after first module completion (if not already authenticated)
    if (!isAuthenticated && conceptId === 'alternative-financial-services') {
      setShowAuthPrompt(true);
      return;
    }

    // If authenticated, save progress to API
    if (isAuthenticated && token) {
      try {
        const response = await fetch(`${API_BASE}/education/submit/${conceptId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            answers: { completed: true },
            score: 100
          })
        });

        if (response.ok) {
          // Update user's trust score
          const trustResponse = await fetch(`${API_BASE}/trust/score`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (trustResponse.ok) {
            const trustData = await trustResponse.json();
            setUser(prev => prev ? {
              ...prev,
              trustScore: trustData.current_score || 0,
              bootcampCompleted: newCompletedConcepts.length >= 6 // Assuming 6 total concepts
            } : prev);
          }
        }
      } catch (error) {
        console.error('Error saving progress to API:', error);
      }
    }
  };

  const handleAuthComplete = async () => {
    setShowAuthPrompt(false);

    // Migrate progress to authenticated user
    if (completedConcepts.length > 0 && token) {
      try {
        const progressData = completedConcepts.map(conceptId => ({
          moduleId: conceptId,
          completed: true,
          score: 100,
          timestamp: Date.now()
        }));

        const response = await fetch(`${API_BASE}/auth/migrate-progress`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ progressData })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Progress migrated:', result);

          // Update user state with new trust score
          const trustResponse = await fetch(`${API_BASE}/trust/score`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (trustResponse.ok) {
            const trustData = await trustResponse.json();
            setUser(prev => prev ? {
              ...prev,
              trustScore: trustData.current_score || 0
            } : prev);
          }
        }
      } catch (error) {
        console.error('Error migrating progress:', error);
      }
    }
  };

  const handleAuthSkip = () => {
    setShowAuthPrompt(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-6xl animate-pulse">🏕️</div>
          <h2 className="text-2xl font-bold text-gray-800">Welcome to TrustPoints</h2>
          <p className="text-gray-600">Loading your financial journey...</p>
        </div>
      </div>
    );
  }

  // Show auth prompt if needed
  if (showAuthPrompt) {
    return (
      <AuthPrompt
        onComplete={handleAuthComplete}
        onSkip={handleAuthSkip}
      />
    );
  }

  if (!user?.bootcampCompleted) {
    return (
      <BootcampLearning
        onComplete={handleBootcampComplete}
        onProgress={handleBootcampProgress}
      />
    );
  }

  // Post-bootcamp phases (placeholder for future development)
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50 p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-8"
        >
          <div className="text-8xl mb-8">🎓</div>

          <div className="bg-white rounded-3xl p-8 shadow-lg border">
            <h1 className="text-4xl font-bold mb-4 text-gray-900">
              Congratulations, {user.name}!
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              You've completed the TrustPoints Financial Bootcamp
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-blue-50 rounded-xl p-6">
                <div className="text-3xl mb-2">🤝</div>
                <h3 className="font-semibold text-gray-900">Trust Score</h3>
                <p className="text-2xl font-bold text-blue-600">{user.trustScore}</p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6">
                <div className="text-3xl mb-2">🏕️</div>
                <h3 className="font-semibold text-gray-900">Bootcamp</h3>
                <p className="text-green-600 font-semibold">✅ Completed</p>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6">
                <div className="text-3xl mb-2">🔑</div>
                <h3 className="font-semibold text-gray-900">Next Phase</h3>
                <p className="text-gray-600 capitalize">{user.currentPhase}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-700 to-orange-700 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Your Journey Continues</h2>
            <p className="text-lg opacity-90 mb-6">
              The next layers of the TrustPoints system are being built.
              You'll be notified when Verification Phase opens.
            </p>
            <div className="text-sm opacity-75">
              🔜 Coming Soon: Identity Verification → Social Trust → Financial Services → Community Governance
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => window.location.reload()}
              className="px-8 py-3 bg-white border-2 border-gray-300 rounded-xl text-gray-700 hover:border-gray-400 transition-colors"
            >
              ← Return to Bootcamp
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};